import React from "react";
import {observer} from "mobx-react-lite";
import {Grid} from "@mui/material";
import {MithraEchart} from "../../../services/echarts/MithraEchart";
import {useSitStores} from "../../../stores";
import type {EChartContextType} from "../../../services/echarts/EChartProvider";
import type {EChartsType} from "echarts/types/dist/shared";
import {lpad} from "../../../utils/js-utils";
import {SIT_TEST} from "../../SitApit";
import i18n from "../../../i18n/i18n";
import {sitMoney} from "../../components/sitMoney";

const fin = (k: string) => i18n.t(`fin.${k}`, {ns: 'sit'});

function init(echart: EChartContextType, root: HTMLElement, data: any[], height: number): EChartsType {
    const chart = echart.init(root, 'mithra', {
        renderer: 'svg',
        height,
    });

    const procurementSavingsChartOption = {
        tooltip: {
            trigger: 'axis',
            valueFormatter: sitMoney,
        },
        toolbox: {
            show: true,
            feature: {
                // dataView: {show: true, readOnly: false},
                // https://echarts.apache.org/en/option.html#toolbox.feature.restore
                // restore: {show: true},
                saveAsImage: {show: true}
            }
        },
        calculable: true,
        legend: {
            data: [fin('target'), fin('actual'), fin('runRate')],
            left: '10%',
            padding: [5, 10, 5, 10],
            textStyle: {
                fontFamily: 'roboto',
                overFlow: 'break'
            }
        },
        color: [
            '#091344',
            '#ff3435',
            '#430944',
            '#0262f2',
            '#08a57a',
            '#a4d8c2',
            '#f3d999',
            '#d3758f',
            '#dcc392',
            '#2e4783',
            '#82b6e9',
            '#ff6347',
            '#a092f1',
            '#0a915d',
            '#eaf889',
            '#6699FF',
            '#ff6666',
            '#3cb371',
            '#d5b158',
            '#38b6b6'
        ],
        dataset: data,  // Pass the data here
        grid: {left: 8, right: 16, top: 48, bottom: 8, containLabel: true},
        xAxis: {
            type: 'time'
        },
        yAxis: {axisLabel: {formatter: sitMoney}},
        series: [
            {
                name: fin('target'),
                type: 'bar',
                encode: {
                    x: 'Saving month',
                    y: 'Acc Target'
                },
                datasetId: 'Target accumulative aggregated by month',
                stack: 'saving',
                color: 'rgb(176,180,190)', // target: neutral grey
                emphasis: {
                    focus: 'series'
                },
                markLine: {
                    data: [
                        {
                            type: 'max',
                            name: fin('targetInYear'),
                            label: {show: true, formatter: '{b}', position: 'end'}
                        }
                    ]
                }
            },
            {
                name: fin('actual'),
                type: 'bar',
                encode: {
                    x: 'Saving month',
                    y: 'Acc Saving'
                },
                datasetId: 'Actual procurement accumulative aggregated savings by month',
                stack: 'target',
                color: '#091344',
                emphasis: {
                    focus: 'series'
                }
            },
            {
                name: fin('runRate'),
                type: 'line',
                smooth: false,
                encode: {
                    x: 'Saving month',
                    y: 'Acc Saving'
                },
                lineStyle: {
                    color: '#430944',
                    width: 2,
                    type: 'dashed'
                },
                emphasis: {
                    focus: 'series'
                },
                datasetId: 'Procurement accumulative aggregated savings by month',
                color: '#430944'
            }
        ]
    };

    chart.setOption(procurementSavingsChartOption);

    return chart;
}

type Props = {
    actualData: {month: Date, value: number}[] | null
    targetData: {month: Date, value: number}[] | null
    height: number
}
export const TargetVsActualDoubleBar: React.FC<Props> = observer(({actualData, targetData, height}) => {
    const {fbStore} = useSitStores();

    if (!actualData || !targetData) {
        return <></>;
    }

    const data = [
        {
            id: 'Procurement accumulative aggregated savings by month',
            dimensions: [
                {name: 'Saving month', type: 'time'},
                {name: 'Acc Saving', type: 'number'}
            ],
            source: actualData.map(d => ([
                `${d.month.getFullYear()}-${lpad(String(d.month.getMonth() + 1), '0', 2)}`,
                d.value,
            ]))
        },
        {
            id: 'Target accumulative aggregated by month',
            dimensions: [
                {name: 'Saving month', type: 'time'},
                {name: 'Acc Target', type: 'number'}
            ],
            source: targetData.map(d => ([
                `${d.month.getFullYear()}-${lpad(String(d.month.getMonth() + 1), '0', 2)}`,
                d.value,
            ]))
        },
        {
            id: 'Actual procurement accumulative aggregated savings by month',
            dimensions: [
                {name: 'Saving month', type: 'time'},
                {name: 'Acc Saving', type: 'number'}
            ],
            source: actualData.map(d => ([
                `${d.month.getFullYear()}-${lpad(String(d.month.getMonth() + 1), '0', 2)}`,
                d.month < fbStore.month ? d.value : 0,
            ]))
        }
    ]
    return (
        <Grid container>
            <Grid size={12}>
                {SIT_TEST && '<TargetVsActualDoubleBar/>'}
                {/*<p>*/}
                {/*    contributionPerMonth={JSON.stringify(actualData)}*/}
                {/*</p>*/}
                {/*<p>*/}
                {/*    data={JSON.stringify(data)}*/}
                {/*</p>*/}
                <MithraEchart
                    // alwaysRerender={() => {
                    // }}
                    initChart={(echart, root) => init(echart, root, data, height)}
                    data={data}
                    autoUpdateDataset
                    loading={fbStore.busy}
                />
            </Grid>
        </Grid>
    );
})